import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, BookOpen, GraduationCap, FlaskConical, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const TYPE_COLORS = {
  GED: { bg: 'bg-[#002FA7]/10', text: 'text-[#002FA7]', border: 'border-[#002FA7]/20', dot: 'bg-[#002FA7]' },
  SAT: { bg: 'bg-[#E53935]/10', text: 'text-[#E53935]', border: 'border-[#E53935]/20', dot: 'bg-[#E53935]' },
  WorkKeys: { bg: 'bg-[#16A34A]/10', text: 'text-[#16A34A]', border: 'border-[#16A34A]/20', dot: 'bg-[#16A34A]' },
  College: { bg: 'bg-[#FFC107]/10', text: 'text-[#7B6100]', border: 'border-[#FFC107]/30', dot: 'bg-[#FFC107]' }
};

const SUBJECT_ICONS = {
  Math: '+ - =',
  Science: 'Sci',
  English: 'Eng',
  'Social Studies': 'SS',
  'SAT Math': 'M',
  'SAT Reading': 'R',
  'SAT Writing': 'W',
  Business: 'Biz',
  Writing: 'Wrt'
};

export default function Schedule() {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/schedule`);
      setSchedule(data);
    } catch (error) {
      console.error('Error fetching schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAllClasses = (day) => {
    if (!schedule) return [];
    let classes = [];
    Object.entries(schedule).forEach(([key, program]) => {
      if (activeFilter !== 'all' && key !== activeFilter) return;
      const dayClasses = program.days[day] || [];
      classes.push(...dayClasses);
    });
    return classes.sort((a, b) => {
      const timeA = a.time.split(' - ')[0];
      const timeB = b.time.split(' - ')[0];
      return timeA.localeCompare(timeB);
    });
  };

  if (loading) {
    return <div className="min-h-[70vh] flex items-center justify-center"><div className="spinner"></div></div>;
  }

  return (
    <div className="py-16 md:py-24 px-4 md:px-6" data-testid="schedule-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#002FA7] mb-4 block">CLASS SCHEDULE</span>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-[#0A0A0A] mb-4">
            Weekly Schedule
          </h1>
          <p className="text-[#4B5563] max-w-xl mx-auto">
            All classes are 1 hour long. Join any class included in your subscription.
          </p>
        </div>

        {/* Legend + Filter */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
          <button
            onClick={() => setActiveFilter('all')}
            className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${activeFilter === 'all' ? 'bg-[#0A0A0A] text-white' : 'bg-white border border-[#E5E7EB] text-[#4B5563] hover:border-[#0A0A0A]'}`}
            data-testid="filter-all-schedule"
          >
            All Programs
          </button>
          {[
            { key: 'ged', label: 'GED', sub: 'Mon-Fri 8-10am' },
            { key: 'sat', label: 'SAT', sub: 'Mon-Fri 12-2pm' },
            { key: 'workkeys', label: 'WorkKeys', sub: 'Mon-Fri 10am-12pm' },
            { key: 'college', label: 'College', sub: 'Mon-Sat 6-10pm' }
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${activeFilter === f.key ? `${TYPE_COLORS[f.label]?.bg} ${TYPE_COLORS[f.label]?.text} border ${TYPE_COLORS[f.label]?.border}` : 'bg-white border border-[#E5E7EB] text-[#4B5563] hover:border-[#0A0A0A]'}`}
              data-testid={`filter-${f.key}-schedule`}
            >
              <span className="block">{f.label}</span>
              <span className="block text-[10px] opacity-70">{f.sub}</span>
            </button>
          ))}
        </div>

        {/* Program Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <div className="bg-white border border-[#E5E7EB] p-6 rounded-sm" data-testid="ged-summary">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#002FA7]/10 rounded-sm flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-[#002FA7]" />
              </div>
              <div>
                <h3 className="font-bold text-[#0A0A0A]">GED Classes</h3>
                <p className="text-xs text-[#4B5563]">Mon-Fri, 8:00 AM - 10:00 AM</p>
              </div>
            </div>
            <p className="text-sm text-[#4B5563]">
              <strong>M/W/F:</strong> Math &middot; <strong>T/Th:</strong> Science, English, Social Studies
            </p>
          </div>
          <div className="bg-white border border-[#E5E7EB] p-6 rounded-sm" data-testid="sat-summary">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#E53935]/10 rounded-sm flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-[#E53935]" />
              </div>
              <div>
                <h3 className="font-bold text-[#0A0A0A]">SAT Prep</h3>
                <p className="text-xs text-[#4B5563]">Mon-Fri, 12:00 PM - 2:00 PM</p>
              </div>
            </div>
            <p className="text-sm text-[#4B5563]">Math, Reading, and Writing & Language</p>
          </div>
          <div className="bg-white border border-[#E5E7EB] p-6 rounded-sm" data-testid="workkeys-summary">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#16A34A]/10 rounded-sm flex items-center justify-center">
                <Award className="w-5 h-5 text-[#16A34A]" />
              </div>
              <div>
                <h3 className="font-bold text-[#0A0A0A]">WorkKeys Prep</h3>
                <p className="text-xs text-[#4B5563]">Mon-Fri, 10:00 AM - 12:00 PM</p>
              </div>
            </div>
            <p className="text-sm text-[#4B5563]">Applied Math, Workplace Documents, Graphic Literacy, Business Writing</p>
          </div>
          <div className="bg-white border border-[#E5E7EB] p-6 rounded-sm" data-testid="college-summary">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#FFC107]/10 rounded-sm flex items-center justify-center">
                <FlaskConical className="w-5 h-5 text-[#7B6100]" />
              </div>
              <div>
                <h3 className="font-bold text-[#0A0A0A]">College Courses</h3>
                <p className="text-xs text-[#4B5563]">Mon-Sat, 6:00 PM - 10:00 PM</p>
              </div>
            </div>
            <p className="text-sm text-[#4B5563]">Math, Science, Business, Writing</p>
          </div>
        </div>

        {/* Weekly Grid */}
        <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-x-auto" data-testid="schedule-grid">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-[#E5E7EB]">
                <th className="p-4 text-left text-xs font-bold text-[#4B5563] uppercase tracking-wider w-24">Day</th>
                <th className="p-4 text-left text-xs font-bold text-[#4B5563] uppercase tracking-wider" colSpan={8}>Classes</th>
              </tr>
            </thead>
            <tbody>
              {DAYS_ORDER.map(day => {
                const classes = getAllClasses(day);
                if (classes.length === 0) return null;
                return (
                  <tr key={day} className="border-b border-[#E5E7EB] last:border-b-0" data-testid={`schedule-row-${day.toLowerCase()}`}>
                    <td className="p-4 align-top">
                      <span className="font-bold text-[#0A0A0A] text-sm">{day.slice(0, 3)}</span>
                      <span className="block text-xs text-[#9CA3AF]">{day}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-wrap gap-2">
                        {classes.map((cls, idx) => {
                          const colors = TYPE_COLORS[cls.type] || TYPE_COLORS.GED;
                          return (
                            <div
                              key={idx}
                              className={`${colors.bg} border ${colors.border} rounded-sm px-3 py-2 min-w-[140px]`}
                              data-testid={`class-${day.toLowerCase()}-${idx}`}
                            >
                              <div className="flex items-center gap-1.5 mb-1">
                                <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                                <span className={`text-xs font-bold ${colors.text}`}>{cls.type}</span>
                              </div>
                              <p className="font-medium text-[#0A0A0A] text-sm">{cls.subject}</p>
                              <p className="text-[10px] text-[#4B5563] flex items-center gap-1 mt-0.5">
                                <Clock className="w-3 h-3" /> {cls.time}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <Link to="/pricing">
            <Button className="bg-[#002FA7] hover:bg-[#001B63] text-white font-bold px-8 py-6 rounded-sm text-base" data-testid="schedule-cta">
              Book Your First Session
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
